import { createContext, useContext, useEffect, useState } from 'react'
import { supabase, signInWithGoogle, signOut, getCurrentUser } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Check current session on mount
    const initAuth = async () => {
      try {
        const { user } = await getCurrentUser()
        setUser(user)
      } catch (err) {
        if (err.name === 'AbortError') return // React 18 StrictMode double-mount, safe to ignore
        console.error('Auth init error:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    initAuth()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const login = async () => {
    setLoading(true)
    setError(null)
    try {
      // Detect in-app browsers (Facebook, Instagram, TikTok, Messenger, etc.)
      // Google blocks OAuth from embedded webviews — must open in real browser
      const ua = navigator.userAgent || ''
      const isInAppBrowser = /FBAN|FBAV|Instagram|Messenger|TikTok|Snapchat|Line\/|Twitter|LinkedIn|Pinterest/i.test(ua)

      if (isInAppBrowser) {
        // Try to force-open in the default browser
        const currentUrl = window.location.href
        // iOS: window.open doesn't escape in-app browser, but we can try
        // Android: intent:// scheme can force Chrome
        if (/android/i.test(ua)) {
          window.location.href = `intent://${currentUrl.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`
        } else {
          // For iOS and others, show a helpful message
          setError('Please open zennago.com in Safari or Chrome to sign in. In-app browsers (Facebook, Instagram, etc.) block Google sign-in. Tap the ••• menu and choose "Open in Safari" or "Open in Browser".')
          setLoading(false)
          return
        }
      }

      const { error } = await signInWithGoogle()
      if (error) throw error
    } catch (err) {
      console.error('Login error:', err)
      setError(err.message)
      setLoading(false)
    }
  }

  const logout = async () => {
    setLoading(true)
    setError(null)
    try {
      const { error } = await signOut()
      if (error) throw error
      setUser(null)
    } catch (err) {
      console.error('Logout error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default useAuth